import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import styles from './summaryRadiusOfOperation.module.scss'
import { TypoGraph, Icon } from 'components'
import MileageRadiusOfOperation from './mileageRadiusOfOperation'
import {
  getRadiusOfOperationMileage,
  getRecentVehicleLocations,
  getMilesByState,
  getMilesByZipcode,
} from 'api'
import {
  RadiusOfOperationMiles,
  RecentVehicleLocationData,
  MilesByStateData,
  MilesByZipcodeData,
  MilesByZipcodeSummary,
} from 'interfaces/ISummaryReport'
import { reportException } from 'tools/logs'
import moment, { Moment } from 'moment'
import strings from 'strings'
import RecentVehicleLocation from './recentVehicleLocation'
import { APIProvider } from '@vis.gl/react-google-maps'
import {
  formatDateMMDDYYYY,
  formatDateToUTC,
  formatDateYYYYMMDD,
} from 'tools/helper'
import MilesByStateMap from './milesByStateMap'
import MilesByZipcodeMap from './milesByZipcodeMap'
import MilesByStateTable from './milesByStateTable'
import MilesByZipCodesTable from './milesByZipCodesTable'

const r = strings.SUMMARY_REPORT
const t = r.RADIUS_OF_OPERATION
const d = strings.TABLE_DEFAULTS

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''

interface ISummaryRadiusOfOperationProps {
  onPdfRefsChange?: (refs: React.RefObject<HTMLDivElement>[]) => void
  onPdfDataChange?: (
    milesByStateData: any[][] | null,
    milesByStateColumns: string[] | null,
    milesByZipcodeData: any[][] | null,
    milesByZipcodeColumns: string[] | null
  ) => void
  onLoadingChange?: (isLoading: boolean) => void
}

const SummaryRadiusOfOperation: React.FC<ISummaryRadiusOfOperationProps> = (
  props
) => {
  const { onPdfRefsChange, onPdfDataChange, onLoadingChange } = props
  const carrierId = localStorage.getItem('carrierId') || '1'
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)

  const recentVehicleLocationRef = useRef<HTMLDivElement>(null)
  const milesByStateMapRef = useRef<HTMLDivElement>(null)
  const milesByZipcodeMapRef = useRef<HTMLDivElement>(null)
  const mileageRadiusOfOperationRef = useRef<HTMLDivElement>(null)

  const [radiusOfOperationMiles, setRadiusOfOperationMiles] =
    useState<RadiusOfOperationMiles | null>(null)
  const [recentVehicleLocations, setRecentVehicleLocations] = useState<
    RecentVehicleLocationData[]
  >([])
  const [milesByStateData, setMilesByStateData] = useState<MilesByStateData[]>(
    []
  )
  const [milesByZipcodeSummary, setMilesByZipcodeSummary] =
    useState<MilesByZipcodeSummary | null>(null)
  const [milesByZipcodeData, setMilesByZipcodeData] = useState<
    MilesByZipcodeData[]
  >([])
  const [hasRadiusOfOperationMiles, setHasRadiusOfOperationMiles] =
    useState<boolean>(false)
  const [hasRecentVehicleLocations, setHasRecentVehicleLocations] =
    useState<boolean>(false)
  const [hasMilesByState, setHasMilesByState] = useState<boolean>(false)
  const [hasMilesByZipcode, setHasMilesByZipcode] = useState<boolean>(false)
  const [loadingRadiusOfOperationMiles, setLoadingRadiusOfOperationMiles] =
    useState<boolean>(false)
  const [loadingRecentVehicleLocations, setLoadingRecentVehicleLocations] =
    useState<boolean>(false)
  const [loadingMilesByState, setLoadingMilesByState] = useState<boolean>(false)
  const [loadingMilesByZipcode, setLoadingMilesByZipcode] =
    useState<boolean>(false)

  useEffect(() => {
    const refs = [
      recentVehicleLocationRef,
      milesByStateMapRef,
      milesByZipcodeMapRef,
      mileageRadiusOfOperationRef,
    ]
    onPdfRefsChange?.(refs)
  }, [onPdfRefsChange])

  const milesByStateColumnsForPdf = useMemo(
    () => [t.STATE, t.COUNTRY, t.MILES, t.PERCENTAGE],
    []
  )

  const milesByZipcodeColumnsForPdf = useMemo(
    () => [t.ZIPCODE, t.CITY, t.COUNTRY, t.MILES, t.PERCENTAGE],
    []
  )

  const milesByStateDataForPdf = useMemo(() => {
    if (!milesByStateData || milesByStateData.length === 0) return null

    return milesByStateData.map((item) => [
      `${item.state.code} - ${item.state.name}`,
      item.state?.country || d.DASH_VALUE,
      item.metrics.distanceMiles.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      }),
      `${item.metrics.percentageOfTotal.toFixed(2)}%`,
    ])
  }, [milesByStateData])

  const milesByZipcodeDataForPdf = useMemo(() => {
    if (!milesByZipcodeData || milesByZipcodeData.length === 0) return null

    const topZipCodes = milesByZipcodeData.slice(
      0,
      Math.min(milesByZipcodeData.length, 25)
    )

    return topZipCodes.map((item) => [
      item.zipCode.zipcode || d.DASH_VALUE,
      `${item.zipCode.city}, ${item.zipCode.stateCode}` || d.DASH_VALUE,
      item.zipCode?.country || d.DASH_VALUE,
      item.metrics.distanceMiles.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      }),
      `${item.metrics.percentageOfTotal.toFixed(2)}%`,
    ])
  }, [milesByZipcodeData])

  useEffect(() => {
    onPdfDataChange?.(
      milesByStateDataForPdf,
      milesByStateDataForPdf ? milesByStateColumnsForPdf : null,
      milesByZipcodeDataForPdf,
      milesByZipcodeDataForPdf ? milesByZipcodeColumnsForPdf : null
    )
  }, [
    milesByStateDataForPdf,
    milesByZipcodeDataForPdf,
    milesByStateColumnsForPdf,
    milesByZipcodeColumnsForPdf,
    onPdfDataChange,
  ])

  useEffect(() => {
    const isLoading =
      loadingRadiusOfOperationMiles ||
      loadingRecentVehicleLocations ||
      loadingMilesByState ||
      loadingMilesByZipcode
    onLoadingChange?.(isLoading)
  }, [
    loadingRadiusOfOperationMiles,
    loadingRecentVehicleLocations,
    loadingMilesByState,
    loadingMilesByZipcode,
    onLoadingChange,
  ])

  useEffect(() => {
    const currentDate = moment().utc()
    const startDate = moment().utc().subtract(90, 'days')
    setStartDate(startDate)
    setEndDate(currentDate)
  }, [])

  useEffect(() => {
    if (startDate && endDate) {
      loadDashboardData()
    }
  }, [startDate, endDate])

  const callGetRecentVehicleLocations = async () => {
    setLoadingRecentVehicleLocations(true)
    try {
      const res = await getRecentVehicleLocations(carrierId, undefined, 5000)
      if (res && res.data) {
        setRecentVehicleLocations(res.data)
        setHasRecentVehicleLocations(res.data.length > 0)
      } else {
        setRecentVehicleLocations([])
        setHasRecentVehicleLocations(false)
      }
    } catch (error) {
      reportException(error)
      setHasRecentVehicleLocations(false)
    } finally {
      setLoadingRecentVehicleLocations(false)
    }
  }

  const callGetMilesByState = async (startDate: string, endDate: string) => {
    setLoadingMilesByState(true)
    try {
      const res = await getMilesByState(carrierId, startDate, endDate)
      if (res && res.breakdownByStates) {
        setMilesByStateData(res.breakdownByStates)
        setHasMilesByState(res.breakdownByStates.length > 0)
      } else {
        setMilesByStateData([])
        setHasMilesByState(false)
      }
    } catch (error) {
      reportException(error)
      setHasMilesByState(false)
    } finally {
      setLoadingMilesByState(false)
    }
  }

  const callGetMilesByZipcode = async (startDate: string, endDate: string) => {
    setLoadingMilesByZipcode(true)
    try {
      const res = await getMilesByZipcode(carrierId, startDate, endDate, -1)
      if (res && res.breakdownByZipCodes) {
        setMilesByZipcodeData(res.breakdownByZipCodes)
        setMilesByZipcodeSummary(res.summary)
        setHasMilesByZipcode(res.breakdownByZipCodes.length > 0)
      } else {
        setMilesByZipcodeData([])
        setMilesByZipcodeSummary(null)
        setHasMilesByZipcode(false)
      }
    } catch (error) {
      reportException(error)
      setHasMilesByZipcode(false)
    } finally {
      setLoadingMilesByZipcode(false)
    }
  }

  const callGetRadiusOfOperationMiles = async (
    startDate: string,
    endDate: string
  ) => {
    setLoadingRadiusOfOperationMiles(true)
    try {
      const res = await getRadiusOfOperationMileage(
        carrierId,
        startDate,
        endDate
      )
      if (res) {
        setRadiusOfOperationMiles(res)
        setHasRadiusOfOperationMiles(true)
      } else {
        setRadiusOfOperationMiles(null)
        setHasRadiusOfOperationMiles(false)
      }
    } catch (error) {
      reportException(error)
      setHasRadiusOfOperationMiles(false)
    } finally {
      setLoadingRadiusOfOperationMiles(false)
    }
  }

  const loadDashboardData = () => {
    if (!startDate || !endDate) return
    const { startDateUTC, endDateUTC } = formatDatesUTC(startDate, endDate)
    const { startDateYYYYMMDD, endDateYYYYMMDD } = formatDatesYYYYMMDD(
      startDate,
      endDate
    )

    Promise.allSettled([
      callGetRecentVehicleLocations(),
      callGetRadiusOfOperationMiles(startDateUTC, endDateUTC),
      callGetMilesByState(startDateYYYYMMDD, endDateYYYYMMDD),
      callGetMilesByZipcode(startDateYYYYMMDD, endDateYYYYMMDD),
    ])
  }

  const formatDatesUTC = (startDate: Moment, endDate: Moment) => ({
    startDateUTC: formatDateToUTC(startDate),
    endDateUTC: formatDateToUTC(endDate),
  })

  const formatDatesYYYYMMDD = (startDate: Moment, endDate: Moment) => ({
    startDateYYYYMMDD: formatDateYYYYMMDD(startDate),
    endDateYYYYMMDD: formatDateYYYYMMDD(endDate),
  })

  const calculatedOnText = `${
    r.CALCULATED_ON +
    formatDateMMDDYYYY(moment().subtract(1, 'days').toISOString())
  } 
      ${r.DAYS_OF_DATA_PRE} ${
    endDate && startDate ? moment(endDate).diff(moment(startDate), 'days') : 0
  } 
          ${r.DAYS_OF_DATA_POST}`

  return (
    <Box className={styles.dashboardContainer}>
      <TypoGraph
        variant="h2"
        sx={{ marginRight: '1rem' }}
        mb={0}
        content={t.RADIUS_OF_OPERATION_TITLE}
      />
      <TypoGraph
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1 }}
      >
        {calculatedOnText}
      </TypoGraph>
      <Box sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          {/* Top Row */}
          <Grid item xs={12} sm={4}>
            <Paper
              id="summary-radius-of-operation-recent-vehicle-locations"
              elevation={0}
              className={styles.panel}
              sx={{ borderRadius: '14px' }}
              ref={recentVehicleLocationRef}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <Icon
                    materialIcon="LocationOnOutlined"
                    color="#dc2626"
                    size={25}
                  />
                </div>
                <Typography variant="h3">
                  {t.RECENT_VEHICLE_LOCATIONS}
                </Typography>
              </div>
              <Box className={styles.panelContentNoPadding}>
                {loadingRecentVehicleLocations ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasRecentVehicleLocations ? (
                  GOOGLE_MAPS_API_KEY ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        height: '100%',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <RecentVehicleLocation data={recentVehicleLocations} />
                      </APIProvider>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {t.NO_MAPS_API_KEY}
                    </Typography>
                  )
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      {t.NO_DATA}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              id="summary-radius-of-operation-miles-by-state"
              elevation={0}
              className={styles.panel}
              sx={{ borderRadius: '14px' }}
              ref={milesByStateMapRef}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <Icon materialIcon="MapOutlined" color="#3b82f6" size={25} />
                </div>
                <Typography variant="h3">{t.MILES_BY_STATE}</Typography>
              </div>
              <Box className={styles.panelContentNoPadding}>
                {loadingMilesByState ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasMilesByState ? (
                  GOOGLE_MAPS_API_KEY ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        height: '100%',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <MilesByStateMap data={milesByStateData} />
                      </APIProvider>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {t.NO_MAPS_API_KEY}
                    </Typography>
                  )
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      {t.NO_DATA}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              id="summary-radius-of-operation-miles-by-zipcode"
              elevation={0}
              className={styles.panel}
              sx={{ borderRadius: '14px' }}
              ref={milesByZipcodeMapRef}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <Icon materialIcon="WarningAmber" color="#f59e0b" size={25} />
                </div>
                <Typography variant="h3">{t.MILES_BY_ZIPCODE}</Typography>
              </div>
              <Box className={styles.panelContentNoPadding}>
                {loadingMilesByZipcode ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasMilesByZipcode ? (
                  GOOGLE_MAPS_API_KEY ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        height: '100%',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <MilesByZipcodeMap
                          summary={milesByZipcodeSummary}
                          data={milesByZipcodeData}
                        />
                      </APIProvider>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {t.NO_MAPS_API_KEY}
                    </Typography>
                  )
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      {t.NO_DATA}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Bottom Row */}
          <Grid item xs={12} sm={4}>
            <Paper
              id="summary-radius-of-operation-mileage-by-radius"
              elevation={0}
              className={styles.panel}
              sx={{ borderRadius: '14px' }}
              ref={mileageRadiusOfOperationRef}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <Icon materialIcon="MapOutlined" color="#8b5cf6" size={25} />
                </div>
                <Typography variant="h3">
                  {t.RADIUS_OF_OPERATION_TITLE}
                </Typography>
              </div>
              <Box className={styles.panelContent}>
                {loadingRadiusOfOperationMiles ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasRadiusOfOperationMiles ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      height: '100%',
                      width: '100%',
                      textAlign: 'left',
                    }}
                  >
                    <MileageRadiusOfOperation data={radiusOfOperationMiles} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      {t.NO_DATA}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper
              id="summary-radius-of-operation-mileage-by-state"
              elevation={0}
              className={styles.panel}
              sx={{ borderRadius: '14px' }}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <Icon
                    materialIcon="LocationOnOutlined"
                    color="#8b5cf6"
                    size={25}
                  />
                </div>
                <Typography variant="h3">{t.MILES_BY_STATE}</Typography>
              </div>
              <Box
                className={styles.panelContentNoPadding}
                sx={{
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                }}
              >
                <MilesByStateTable
                  loading={loadingMilesByState}
                  hasData={hasMilesByState}
                  data={milesByStateData}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              id="summary-radius-of-operation-top-zipcodes-by-activity"
              elevation={0}
              className={styles.panel}
              sx={{ borderRadius: '14px' }}
            >
              <div className={styles.panelHeader}>
                <div className={styles.panelIcon}>
                  <Icon materialIcon="TrendingUp" color="#8b5cf6" size={25} />
                </div>
                <Typography variant="h3">
                  {t.TOP_ZIPCODES_BY_ACTIVITY}
                </Typography>
              </div>
              <Box
                className={styles.panelContentNoPadding}
                sx={{
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                }}
              >
                <MilesByZipCodesTable
                  loading={loadingMilesByZipcode}
                  hasData={hasMilesByZipcode}
                  data={milesByZipcodeData}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default SummaryRadiusOfOperation
